import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getToken } from '../context/AuthContext';
import PremiumPrompt from '../components/PremiumPrompt';
import config from '../config';
import { useFlags } from 'launchdarkly-react-client-sdk';

function fmt(val, decimals = 2) {
  if (val === null || val === undefined) return '—';
  return Number(val).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtPrice(val) {
  if (val === null || val === undefined) return '—';
  return '$' + fmt(val);
}

function getScoreColor(score) {
  if (score >= 4.5) return '#1a9e5c';
  if (score >= 3.5) return '#2ecc71';
  if (score >= 2.5) return '#f39c12';
  if (score >= 1.5) return '#e67e22';
  return '#e74c3c';
}

function ValueScoreBadge({ score, label, color }) {
  if (score === null || score === undefined) return <span>—</span>;
  return (
    <span className="watchlist-score-badge" style={{ color: color || '#333' }}>
      {Number(score).toFixed(1)}
    </span>
  );
}

export default function WatchlistPage() {
  const { user, isSubscribed } = useAuth();
  const { premiumFeaturesEnabled } = useFlags();
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const hasPremium = isSubscribed || premiumFeaturesEnabled;

  useEffect(() => {
    if (!user || !hasPremium) return;
    fetch(`${config.apiBaseURL}/api/watchlists`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => r.json())
      .then(data => {
        setStocks(data.stocks || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load watchlist');
        setLoading(false);
      });
  }, [user, hasPremium]);

  if (!user || !hasPremium) {
    return (
      <div className="watchlist-page">
        <PremiumPrompt feature="Watchlists" />
      </div>
    );
  }

  const removeStock = async (symbol) => {
    await fetch(`${config.apiBaseURL}/api/watchlists/stocks/${symbol}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    setStocks(prev => prev.filter(s => s.symbol !== symbol));
  };

  const scoredStocks = stocks.filter(s => s.value_score !== null && s.value_score !== undefined);
  const avgScore = scoredStocks.length
    ? scoredStocks.reduce((sum, s) => sum + parseFloat(s.value_score), 0) / scoredStocks.length
    : null;

  return (
    <div className="watchlist-page">
      <h2>My Watchlist</h2>

      {loading && <p>Loading…</p>}
      {error && <div className="auth-error">{error}</div>}

      {!loading && avgScore !== null && (
        <div className="watchlist-avg-score">
          <span className="watchlist-avg-label">Watchlist Value Score</span>
          <span className="watchlist-avg-value" style={{ color: getScoreColor(avgScore) }}>
            {avgScore.toFixed(1)}
          </span>
          <span className="watchlist-avg-sub">avg across {scoredStocks.length} stock{scoredStocks.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {!loading && stocks.length === 0 && (
        <p className="watchlist-empty">
          Your watchlist is empty. Click "Add to Watchlist" on any stock page to get started.
        </p>
      )}

      {stocks.length > 0 && (
        <div className="watchlist-table-wrapper">
          <table className="watchlist-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Value Score</th>
                <th>Price</th>
                <th>Change ($)</th>
                <th>Change (%)</th>
                <th>52W High</th>
                <th>52W Low</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {stocks.map(s => {
                const pct = parseFloat(s.change_percent) || 0;
                const price = parseFloat(s.price) || 0;
                const dollarChange = price - price / (1 + pct / 100);
                const isPositive = pct >= 0;

                return (
                  <tr key={s.symbol}>
                    <td>
                      <button className="btn-link" onClick={() => navigate(`/${s.symbol}`)}>
                        {s.symbol}
                      </button>
                    </td>
                    <td>
                      <ValueScoreBadge score={s.value_score} label={s.value_label} color={s.value_color} />
                    </td>
                    <td>{fmtPrice(s.price)}</td>
                    <td className={isPositive ? 'change-positive' : 'change-negative'}>
                      {isPositive ? '+' : ''}{fmtPrice(dollarChange)}
                    </td>
                    <td className={isPositive ? 'change-positive' : 'change-negative'}>
                      {isPositive ? '+' : ''}{fmt(pct)}%
                    </td>
                    <td>{fmtPrice(s.week52_high)}</td>
                    <td>{fmtPrice(s.week52_low)}</td>
                    <td>
                      <button className="btn-remove" onClick={() => removeStock(s.symbol)}>Remove</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
